import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, Text, Image, useToast, Box, Badge, HStack,
  PinInput, PinInputField, SimpleGrid, Divider,
} from '@chakra-ui/react';
import { FiCopy, FiRefreshCw } from 'react-icons/fi';
import api from '../api';
import ConfirmModal from './ConfirmModal';

export default function MfaSetupModal({ isOpen, onClose, mfaEnabled, onUpdate }) {
  const [qrData, setQrData] = useState(null);
  const [code, setCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [backupCount, setBackupCount] = useState(0);
  const [showDisable, setShowDisable] = useState(false);
  const [showRegen, setShowRegen] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (isOpen && mfaEnabled) {
      api.get('/auth/mfa/backup-count').then(r => setBackupCount(r.data.count)).catch(() => {});
    }
    if (isOpen) { setBackupCodes(null); setQrData(null); setCode(''); }
  }, [isOpen, mfaEnabled]);

  const handleSetup = async () => {
    try {
      const { data } = await api.post('/auth/mfa/setup');
      setQrData(data);
    } catch(e) { toast({ title: 'Failed to start MFA setup', status: 'error', duration: 2000 }); }
  };

  const handleVerify = async () => {
    if (code.length < 6) return;
    setSaving(true);
    try {
      const { data } = await api.post('/auth/mfa/verify', { code });
      toast({ title: 'MFA enabled successfully', status: 'success', duration: 2000 });
      setBackupCodes(data.backup_codes);
      setQrData(null); setCode('');
      if (onUpdate) onUpdate(true);
    } catch(e) {
      toast({ title: e.response?.data?.error || 'Invalid code', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  const handleDisable = async (password) => {
    try {
      await api.post('/auth/mfa/disable', { password });
      toast({ title: 'MFA disabled', status: 'info', duration: 2000 });
      setShowDisable(false); setBackupCodes(null);
      if (onUpdate) onUpdate(false);
      onClose();
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
  };

  const handleRegen = async (password) => {
    try {
      const { data } = await api.post('/auth/mfa/regenerate-backup', { password });
      setBackupCodes(data.backup_codes);
      setBackupCount(data.backup_codes.length);
      setShowRegen(false);
      toast({ title: 'New backup codes generated', status: 'success', duration: 2000 });
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
  };

  const copyBackupCodes = () => {
    if (!backupCodes) return;
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast({ title: 'Backup codes copied to clipboard', status: 'info', duration: 1500 });
  };

  // Backup codes display
  const BackupCodesDisplay = () => (
    <Box bg="yellow.50" border="1px" borderColor="yellow.300" p={4} borderRadius="md" w="100%">
      <Text fontWeight="700" color="yellow.800" mb={2}>Save Your Backup Codes</Text>
      <Text fontSize="12px" color="gray.600" mb={3}>
        Each code can only be used once. Store them somewhere safe. If you lose your authenticator app, you can use these codes to log in.
      </Text>
      <SimpleGrid columns={2} spacing={2} mb={3}>
        {backupCodes.map((c, i) => (
          <Box key={i} bg="white" border="1px" borderColor="gray.200" borderRadius="md" px={3} py={1} textAlign="center">
            <Text fontFamily="mono" fontWeight="700" letterSpacing="0.1em">{c}</Text>
          </Box>
        ))}
      </SimpleGrid>
      <HStack spacing={2}>
        <Button size="sm" leftIcon={<FiCopy />} variant="outline" onClick={copyBackupCodes}>
          Copy All
        </Button>
        <Text fontSize="10px" color="gray.400">These codes will not be shown again.</Text>
      </HStack>
    </Box>
  );

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered closeOnOverlayClick={!backupCodes}>
      <ModalOverlay /><ModalContent>
        <ModalHeader>Two-Factor Authentication (MFA)</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Just enabled — show backup codes */}
          {backupCodes ? (
            <VStack spacing={3}>
              <Badge colorScheme="green" fontSize="sm" px={3} py={1}>MFA Enabled</Badge>
              <BackupCodesDisplay />
            </VStack>

          /* Already enabled — show status + management */
          ) : mfaEnabled && !qrData ? (
            <VStack spacing={3}>
              <Badge colorScheme="green" fontSize="sm" px={3} py={1}>MFA is Enabled</Badge>
              <Text color="gray.500" textAlign="center">
                Your account is protected with two-factor authentication.
              </Text>
              <Box bg="gray.50" p={3} borderRadius="md" w="100%" textAlign="center">
                <Text fontSize="12px" color="gray.600">
                  Backup codes remaining: <Badge colorScheme={backupCount > 3 ? 'green' : backupCount > 0 ? 'orange' : 'red'}>{backupCount}</Badge>
                </Text>
                {backupCount <= 3 && (
                  <Text fontSize="11px" color="orange.500" mt={1}>
                    {backupCount === 0 ? 'No backup codes left! Regenerate now.' : 'Running low on backup codes.'}
                  </Text>
                )}
              </Box>
              <Divider />
              <HStack spacing={2}>
                <Button size="sm" leftIcon={<FiRefreshCw />} colorScheme="blue" variant="outline"
                  onClick={() => setShowRegen(true)}>
                  Regenerate Backup Codes
                </Button>
                <Button size="sm" colorScheme="red" variant="outline" onClick={() => setShowDisable(true)}>
                  Disable MFA
                </Button>
              </HStack>
            </VStack>

          /* Not enabled, not setting up — show setup prompt */
          ) : !qrData ? (
            <VStack spacing={3}>
              <Text color="gray.500" textAlign="center">
                Add an extra layer of security with an authenticator app (Google Authenticator, Authy, etc).
              </Text>
              <Button bg="brand.600" color="white" _hover={{ bg:'brand.700' }} onClick={handleSetup}>
                Set Up MFA
              </Button>
            </VStack>

          /* Setting up — show QR + verify */
          ) : (
            <VStack spacing={4}>
              <Text fontWeight="600">1. Scan this QR code with your authenticator app</Text>
              <Image src={qrData.qr_code} alt="QR Code" w="200px" h="200px" borderRadius="md" border="1px" borderColor="gray.200" />
              <Box bg="gray.50" p={2} borderRadius="md" w="100%">
                <Text fontSize="10px" color="gray.500" mb={1}>Or enter this key manually:</Text>
                <Text fontSize="12px" fontFamily="mono" fontWeight="600" userSelect="all">{qrData.secret}</Text>
              </Box>
              <Text fontWeight="600">2. Enter the 6-digit code from your app</Text>
              <HStack>
                <PinInput otp size="lg" value={code} onChange={setCode} autoFocus>
                  <PinInputField /><PinInputField /><PinInputField />
                  <PinInputField /><PinInputField /><PinInputField />
                </PinInput>
              </HStack>
              <Button bg="brand.600" color="white" _hover={{ bg:'brand.700' }}
                onClick={handleVerify} isLoading={saving} isDisabled={code.length < 6}>
                Verify &amp; Enable
              </Button>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" onClick={onClose}>
            {backupCodes ? 'I\'ve saved my codes' : 'Close'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    <ConfirmModal isOpen={showDisable} onClose={() => setShowDisable(false)}
      title="Disable MFA" message="Enter your password to disable two-factor authentication."
      showInput inputLabel="Password" inputPlaceholder="Enter your password..."
      confirmLabel="Disable MFA" confirmColor="red"
      onConfirm={(pw) => handleDisable(pw)} />
    <ConfirmModal isOpen={showRegen} onClose={() => setShowRegen(false)}
      title="Regenerate Backup Codes" message="This will invalidate all existing backup codes and generate 10 new ones. Enter your password to confirm."
      showInput inputLabel="Password" inputPlaceholder="Enter your password..."
      confirmLabel="Regenerate" confirmColor="blue"
      onConfirm={(pw) => handleRegen(pw)} />
    </>
  );
}
